/**
 * Pumping programs: the list with live progress and Start / Pause / Stop, and the editor,
 * laid out in the same order as FMS-Control's pumping program manual:
 * start conditions → start actions → steps → stop conditions → stop actions.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, type Device, type Program, type ProgramDraft, type ProgramView, type Reading } from "../api.ts";
import { boardPorts, can } from "../control.ts";
import { milesightOutputs } from "../../../../packages/drivers/src/milesight-commands.ts";
import { clock, isFmsBoard, portKey, portLabel } from "../format.ts";
import { clockText, DAY_NAMES, hms, minutesText, programsPollMs, ruleText, startText, STATE_TEXT, whereText } from "../programs-text.ts";
import { stepsLength } from "../../../../packages/core/src/programs.ts";
import { go } from "../route.ts";
import { LockCard, useCan } from "../plan.tsx";
import { HelpTip, Icon, Problem, Section, Spinner, useLoad, useNow } from "../ui.tsx";
import { RulesList } from "./rules.tsx";
import { isRule } from "../../../../packages/core/src/rules.ts";
import { isOnOff } from "../../../../packages/core/src/ports.ts";

type Action = Program["startActions"][number];
type Step = Program["steps"][number];
type StartCond = Program["start"][number];
type StopCond = Program["stopConditions"][number];

const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");

// ------------------------------------------------------------------ list

export function Programs({ accountId, staff, role }: { accountId: string | undefined; staff: boolean; role: string }) {
  const allowed = useCan("programs");
  if (!allowed) {
    return (
      <Section title="Programs">
        <LockCard feature="programs" />
      </Section>
    );
  }
  return <ProgramsBody accountId={accountId} staff={staff} role={role} />;
}

function ProgramsBody({ accountId, staff, role }: { accountId: string | undefined; staff: boolean; role: string }) {
  const id = staff ? accountId : undefined;
  const [poll, setPoll] = useState(30_000);
  const list = useLoad(() => api.programs(id), [id], poll);
  useEffect(() => setPoll(programsPollMs(list.data?.programs, 5_000)), [list.data]);
  const devices = useLoad(() => api.devices(id), [id]);
  const [view, setView] = useState<"programs" | "rules">(() => {
    try {
      return localStorage.getItem("tc.programs.view") === "rules" ? "rules" : "programs";
    } catch {
      return "programs";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("tc.programs.view", view);
    } catch {
      /* not kept */
    }
  }, [view]);
  const now = useNow(5_000);
  if (list.error && !list.data) return <Problem error={list.error} onRetry={list.reload} />;
  if (!list.data || !devices.data) return <Spinner />;
  const dev = (eui: string) => devices.data!.devices.find((d) => d.devEui === eui);
  const canEdit = can(role, "edit-rules");
  const ruleCount = list.data.programs.filter((v) => isRule(v.program)).length;
  const tabs = (
    <div className="chips" role="tablist" aria-label="Programs or rules">
      <button className="chip" role="tab" aria-pressed={view === "programs"} onClick={() => setView("programs")}>
        All programs
      </button>
      <button className="chip" role="tab" aria-pressed={view === "rules"} onClick={() => setView("rules")}>
        Rules ({ruleCount})
      </button>
    </div>
  );
  if (view === "rules")
    return (
      <Section title="Rules" right={tabs}>
        {!list.data.running && (
          <div className="banner warn small">Rules don't switch anything yet: Think-Control is only listening while FMS-Control still runs these devices. Set them up now; they work at cut-over.</div>
        )}
        <RulesList programs={list.data.programs} devices={devices.data.devices} accountId={id} role={role} onChanged={list.reload} />
      </Section>
    );
  return (
    <Section
      title="Programs"
      right={
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {tabs}
          {canEdit && (
            <button className="btn primary" onClick={() => go({ name: "program", id: "new" })}>
              + New program
            </button>
          )}
        </div>
      }
    >
      {!list.data.running && (
        <div className="banner warn small">
          Programs don't run yet: Think-Control is only listening while FMS-Control still runs these devices. You can set them up now; they start working at
          cut-over.
        </div>
      )}
      {!list.data.programs.length && (
        <div className="card empty">
          No programs yet.
          {canEdit ? " A program runs pumps and valves in order: for example open the valves, run the pump for 6 hours, then close the valves." : ""}
        </div>
      )}
      {list.data.programs.map((v) => (
        <ProgramCard key={v.program.id} v={v} dev={dev} now={now} role={role} running={list.data!.running} onChanged={list.reload} />
      ))}
    </Section>
  );
}

function ProgramCard({ v, dev, now, role, running, onChanged }: { v: ProgramView; dev: (eui: string) => Device | undefined; now: number; role: string; running: boolean; onChanged: () => void }) {
  const { program: p, run } = v;
  const st = STATE_TEXT[run.state];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [asking, setAsking] = useState<"start" | "stop" | null>(null);
  const [showLog, setShowLog] = useState(false);
  const act = async (a: "start" | "pause" | "resume" | "stop" | "reset") => {
    setBusy(true);
    setError("");
    setAsking(null);
    try {
      await api.programAction(p.id, a);
      onChanged();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const control = can(role, "control");
  const active = run.state === "running" || run.state === "pausing" || run.state === "stopping";
  const where = whereText(dev, p, run, now);
  return (
    <div className="card prog">
      <div className="prog-top">
        <h3>{p.name}</h3>
        <span className={`pill ${st.tone}`}>{st.label}</span>
        {!p.enabled && <span className="pill">Off</span>}
        <span className="muted small" style={{ marginLeft: "auto" }}>
          {minutesText(stepsLength(p))} · {p.steps.length} step{p.steps.length === 1 ? "" : "s"}
        </span>
      </div>
      {(active || run.state === "paused") && (
        <div className="progress" aria-label={`${v.progress}% done`}>
          <div style={{ width: `${v.progress}%`, background: run.state === "paused" ? "var(--warn)" : undefined }} />
        </div>
      )}
      {where && <div className="small">{where}</div>}
      {run.reason && run.state !== "running" && <div className={`small ${run.state === "failed" ? "error" : "muted"}`}>{cap(run.reason)}</div>}
      <div className="prog-meta">
        <span>
          <b>Starts:</b> {startText(dev, p)}
        </span>
        {p.stopConditions.length > 0 && (
          <span>
            <b>Stops:</b> {p.stopConditions.map((c) => `${c.pause ? "pause" : "stop"} if ${ruleText(dev, c)}`).join("; ")}
          </span>
        )}
      </div>
      {error && <div className="error small">{error}</div>}
      <div className="toolbar">
        {control && asking && (
          <>
            <button className={`btn ${asking === "start" ? "primary" : "danger"}`} disabled={busy} onClick={() => void act(asking)}>
              Confirm: {asking} {p.name}
            </button>
            <button className="btn" onClick={() => setAsking(null)}>
              Cancel
            </button>
          </>
        )}
        {control && !asking && (
          <>
            {!active && run.state !== "paused" && (
              <button className="btn primary" disabled={busy || !running} onClick={() => setAsking("start")}>
                Start now
              </button>
            )}
            {run.state === "running" && (
              <button className="btn" disabled={busy} onClick={() => void act("pause")}>
                Pause
              </button>
            )}
            {run.state === "paused" && (
              <button className="btn primary" disabled={busy || !running} onClick={() => void act("resume")}>
                Resume
              </button>
            )}
            {(active || run.state === "paused") && (
              <button className="btn danger" disabled={busy || run.state === "stopping"} onClick={() => setAsking("stop")}>
                Stop
              </button>
            )}
            {(run.state === "failed" || run.state === "stopped") && (
              <button className="btn" disabled={busy} onClick={() => void act("reset")}>
                Reset
              </button>
            )}
          </>
        )}
        <span style={{ flex: 1 }} />
        <button className="btn ghost" onClick={() => setShowLog(!showLog)}>
          {showLog ? "Hide log" : "Log"}
        </button>
        {can(role, "edit-rules") && (
          <button className="btn" disabled={active || run.state === "paused"} title={active ? "Stop it to change it" : ""} onClick={() => go({ name: "program", id: p.id })}>
            Edit
          </button>
        )}
      </div>
      {showLog && (
        <div className="log">
          {!run.log.length && <div className="muted small">Nothing yet.</div>}
          {[...run.log].reverse().map((l, i) => (
            <div key={i}>
              <time>{clock(l.at, now)}</time> {l.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ------------------------------------------------------------------ editor

const blank = (): ProgramDraft => ({
  name: "",
  enabled: true,
  timezone: "Australia/Brisbane",
  start: [{ type: "schedule", days: [1, 2, 3, 4, 5, 6, 0], time: "22:00" }],
  startActions: [],
  steps: [],
  stopConditions: [],
  stopActions: [],
});

export function ProgramEditor({ id, accountId, staff }: { id: string; accountId: string | undefined; staff: boolean }) {
  const acct = staff ? accountId : undefined;
  const isNew = id === "new";
  const list = useLoad(() => (isNew ? Promise.resolve(null) : api.programs(acct)), [id, acct]);
  const devices = useLoad(() => api.devices(acct), [acct]);
  if (list.error) return <Problem error={list.error} />;
  if ((!isNew && !list.data) || !devices.data) return <Spinner />;
  const existing = list.data?.programs.find((v) => v.program.id === id)?.program;
  if (!isNew && !existing) return <div className="card empty">That program doesn't exist any more.</div>;
  const { id: _i, accountId: _a, updatedAt: _u, updatedBy: _b, ...draft } = existing ?? { ...blank(), id: "", accountId: "", updatedAt: 0, updatedBy: "" };
  return <Editor initial={draft} id={isNew ? null : id} accountId={acct} devices={devices.data.devices} />;
}

function Editor({ initial, id, accountId, devices }: { initial: ProgramDraft; id: string | null; accountId: string | undefined; devices: Device[] }) {
  const [p, setP] = useState<ProgramDraft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [asking, setAsking] = useState(false);
  // Devices with outputs a program can switch: FMS boards, and Milesight controllers (UC300 DO,
  // UC50x GPIO, UC51x and QOTO valves), which the runner switches with their own downlinks.
  const boards = devices.filter((d) => isFmsBoard(d.model) || milesightOutputs(d.model).length > 0);
  const dev = (eui: string) => devices.find((d) => d.devEui === eui);
  const set = <K extends keyof ProgramDraft>(k: K, v: ProgramDraft[K]) => setP((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      if (id) await api.updateProgram(id, p);
      else await api.createProgram(p, accountId);
      go({ name: "programs" });
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const firstOut = (): Pick<Action, "devEui" | "kind" | "channel"> => ({ devEui: boards[0]?.devEui ?? "", kind: "ro", channel: 1 });
  return (
    <>
      <div>
        <button className="btn ghost" style={{ paddingLeft: 0 }} onClick={() => go({ name: "programs" })}>
          <Icon name="back" size={18} /> Programs
        </button>
      </div>
      <div className="card pad" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ fontSize: 22 }}>{id ? `Edit ${initial.name}` : "New program"}</h2>
        <div className="frow">
          <div className="field">
            <label htmlFor="pName">Name</label>
            <input id="pName" value={p.name} maxLength={60} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Night fill, Pivot block A" />
          </div>
          <label className="checkline" style={{ alignSelf: "end", paddingBottom: 10 }}>
            <input type="checkbox" checked={p.enabled} onChange={(e) => set("enabled", e.target.checked)} />
            <span>On: start by itself when a start condition is met</span>
          </label>
        </div>
        {!boards.length && <div className="banner warn small">Programs switch outputs on FMS boards. There are none on this account yet.</div>}
      </div>

      <Part n={1} title="Start conditions" hint="When it starts by itself. Any one of these starts it. Leave empty to start it by hand only.">
        {p.start.map((c, i) => (
          <StartRow key={i} c={c} devices={devices} onChange={(v) => set("start", p.start.map((x, j) => (j === i ? v : x)))} onRemove={() => set("start", p.start.filter((_, j) => j !== i))} />
        ))}
        <div className="toolbar">
          <button className="btn" onClick={() => set("start", [...p.start, { type: "schedule", days: [1, 3, 5], time: "05:00" }])}>
            + Schedule
          </button>
          <button className="btn" onClick={() => set("start", [...p.start, { type: "once", at: Date.now() + 3_600_000 }])}>
            + Set time
          </button>
          <button className="btn" disabled={!devices.length} onClick={() => set("start", [...p.start, { type: "rule", devEui: devices[0]!.devEui, port: "", op: "<", value: 0, forSec: 60 }])}>
            + Reading
          </button>
        </div>
      </Part>

      <Part n={2} title="Start actions" hint="Done first, in order, each confirmed by the device: e.g. open the valves to a paddock, set the pump speed.">
        <Actions list={p.startActions} boards={boards} onChange={(v) => set("startActions", v)} add={() => set("startActions", [...p.startActions, { ...firstOut(), state: true, waitSec: 0 }])} />
      </Part>

      <Part n={3} title="Steps" hint="The main event, as in FMS-Control. Each step starts at its own time, counted from when the steps begin (after the start actions), and runs for its run time. Steps can overlap or start together. Run time counts from when the device confirms it's on.">
        {p.steps.map((s, i) => (
          <StepRow key={i} s={s} boards={boards} onChange={(v) => set("steps", p.steps.map((x, j) => (j === i ? v : x)))} onRemove={() => set("steps", p.steps.filter((_, j) => j !== i))} />
        ))}
        <div className="toolbar">
          {/* A new step starts when the last one ends: zones back to back unless changed. */}
          <button className="btn" disabled={!boards.length} onClick={() => set("steps", [...p.steps, { ...firstOut(), kind: "ro", waitSec: Math.round(stepsLength(p) * 60), runMin: 60 }])}>
            + Step
          </button>
          {p.steps.length > 0 && (
            <span className="muted small">Takes {minutesText(stepsLength(p))} from the first step starting to the last one finishing</span>
          )}
        </div>
      </Part>

      <Part n={4} title="Stop conditions" hint="If any of these is true for its time, the program stops, or pauses and carries on from the same point when it clears. Stop actions run either way.">
        {p.stopConditions.map((c, i) => (
          <RuleRow
            key={i}
            c={c}
            devices={devices}
            onChange={(v) => set("stopConditions", p.stopConditions.map((x, j) => (j === i ? { ...x, ...v } : x)))}
            onRemove={() => set("stopConditions", p.stopConditions.filter((_, j) => j !== i))}
            extra={
              <div className="field">
                <label>Then</label>
                <select value={c.pause ? "pause" : "stop"} onChange={(e) => set("stopConditions", p.stopConditions.map((x, j) => (j === i ? { ...x, pause: e.target.value === "pause" } : x)))}>
                  <option value="pause">Pause</option>
                  <option value="stop">Stop</option>
                </select>
              </div>
            }
          />
        ))}
        <div className="toolbar">
          <button className="btn" disabled={!devices.length} onClick={() => set("stopConditions", [...p.stopConditions, { devEui: devices[0]!.devEui, port: "", op: ">", value: 0, forSec: 30, pause: false }])}>
            + Stop condition
          </button>
        </div>
      </Part>

      <Part n={5} title="Stop actions" hint="Done after the steps finish, or when it's paused or stopped. Usually the start actions reversed: close the valves.">
        <Actions list={p.stopActions} boards={boards} onChange={(v) => set("stopActions", v)} add={() => set("stopActions", [...p.stopActions, { ...firstOut(), state: false, waitSec: 0 }])} />
        {p.startActions.length > 0 && !p.stopActions.length && (
          <button
            className="btn ghost"
            onClick={() => set("stopActions", [...p.startActions].reverse().map((a) => (a.kind === "ao" ? { ...a, value: 0, waitSec: 0 } : { ...a, state: !a.state, waitSec: 0 })))}
          >
            Fill in from the start actions, reversed
          </button>
        )}
      </Part>

      <div className="card pad" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="banner info small">
          Safety rules and timers on each board keep working whatever a program does. For a pump, a board safety timer a little longer than its run time is a good backstop.
        </div>
        {error && <div className="error">{error}</div>}
        <div className="toolbar">
          {asking ? (
            <>
              <button className="btn primary" disabled={busy} onClick={() => void save()}>
                {busy ? "Saving…" : p.enabled && p.start.length ? "Confirm: save; it will start by itself" : "Confirm: save"}
              </button>
              <button className="btn" onClick={() => setAsking(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button className="btn primary" onClick={() => setAsking(true)}>
              Save program
            </button>
          )}
          <button className="btn" onClick={() => go({ name: "programs" })}>
            Cancel
          </button>
          {id && <DeleteProgram id={id} />}
        </div>
      </div>
    </>
  );
}

function DeleteProgram({ id }: { id: string }) {
  const [error, setError] = useState("");
  return (
    <>
      <span style={{ flex: 1 }} />
      <button
        className="btn danger"
        onClick={async () => {
          if (!confirm("Delete this program? This can't be undone.")) return;
          try {
            await api.deleteProgram(id);
            go({ name: "programs" });
          } catch (e) {
            setError(message(e));
          }
        }}
      >
        Delete
      </button>
      {error && <span className="error small">{error}</span>}
    </>
  );
}

function Part({ n, title, hint, children }: { n: number; title: string; hint: string; children: ReactNode }) {
  return (
    <section className="card pad part">
      <div className="part-head">
        <span className="part-n">{n}</span>
        <div>
          <h3>{title}</h3>
          <div className="muted small">{hint}</div>
        </div>
      </div>
      {children}
    </section>
  );
}

function StartRow({ c, devices, onChange, onRemove }: { c: StartCond; devices: Device[]; onChange: (c: StartCond) => void; onRemove: () => void }) {
  if (c.type === "schedule") {
    return (
      <div className="frow cond">
        <div className="field">
          <label>Days</label>
          <div className="chips">
            {DAY_NAMES.map((d, i) => (
              <button key={d} className="chip" aria-pressed={c.days.includes(i)} onClick={() => onChange({ ...c, days: c.days.includes(i) ? c.days.filter((x) => x !== i) : [...c.days, i] })}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label>At</label>
          <input type="time" value={c.time} onChange={(e) => onChange({ ...c, time: e.target.value })} />
        </div>
        <RemoveBtn onRemove={onRemove} />
      </div>
    );
  }
  if (c.type === "once") {
    const local = new Date(c.at - new Date(c.at).getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
    return (
      <div className="frow cond">
        <div className="field">
          <label>Once, at</label>
          <input type="datetime-local" value={local} onChange={(e) => e.target.value && onChange({ ...c, at: new Date(e.target.value).getTime() })} />
        </div>
        <RemoveBtn onRemove={onRemove} />
      </div>
    );
  }
  return <RuleRow c={c} devices={devices} onChange={(v) => onChange({ ...c, ...v })} onRemove={onRemove} />;
}

/** A device reading compared with a value, held for a time. Ports come from what the device reports. */
function RuleRow<T extends { devEui: string; port: string; op: StopCond["op"]; value?: number; value2?: number; forSec: number }>(props: {
  c: T;
  devices: Device[];
  onChange: (v: Partial<T>) => void;
  onRemove: () => void;
  extra?: ReactNode;
}) {
  const { c, devices } = props;
  const latest = useLoad(() => (c.devEui ? api.latest(c.devEui).then((r) => r.readings) : Promise.resolve([] as Reading[])), [c.devEui]);
  const d = devices.find((x) => x.devEui === c.devEui);
  const ports = useMemo(() => {
    const keys = new Set<string>([...(d?.ports.filter((p) => p.enabled).map(portKey) ?? []), ...(latest.data ?? []).map((r) => r.port)]);
    keys.delete("snr");
    // Sensors first: stop and start conditions are usually about a reading, not an output.
    const out = (k: string) => Number(/^(do|ro)_?\d|^valve_|^gpio_out/.test(k));
    return [...keys].sort((a, b) => out(a) - out(b));
  }, [d, latest.data]);
  useEffect(() => {
    if (!c.port && ports[0]) props.onChange({ port: ports[0] } as Partial<T>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ports]);
  // Milesight ports are di_1 / do_1 / gpio_1 / valve_1, FMS boards di1 / do1: isOnOff knows both.
  // An imported "is off" on a UC300 di_1 was being turned into "is above (blank)" when opened.
  const digital = isOnOff(d ?? { model: "" }, c.port) || /^(di|do|ro)\d+$/.test(c.port);
  // Keep the condition sensible for the kind of reading: on/off for switches, above/below for values.
  useEffect(() => {
    // Only once the device is known: never rewrite a saved condition on a board we can't see.
    if (!c.port || !d) return;
    if (digital && c.op !== "on" && c.op !== "off") props.onChange({ op: "on" } as Partial<T>);
    if (!digital && (c.op === "on" || c.op === "off")) props.onChange({ op: ">" } as Partial<T>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.port, digital]);
  const unit = latest.data?.find((r) => r.port === c.port)?.unit ?? "";
  return (
    <div className="frow cond">
      <div className="field">
        <label>Device</label>
        <select value={c.devEui} onChange={(e) => props.onChange({ devEui: e.target.value, port: "" } as Partial<T>)}>
          {/* A board that isn't on this farm any more (archived, or moved): shown as it is, never silently swapped for the first one. */}
          {!devices.some((x) => x.devEui === c.devEui) && <option value={c.devEui}>Not on this farm ({c.devEui})</option>}
          {devices.map((x) => (
            <option key={x.devEui} value={x.devEui}>
              {x.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Reading</label>
        <select value={c.port} onChange={(e) => props.onChange({ port: e.target.value } as Partial<T>)}>
          {!ports.length && <option value="">Nothing reported yet</option>}
          {ports.map((k) => (
            <option key={k} value={k}>
              {d ? portLabel(d, k) : k}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Is</label>
        <select value={c.op} onChange={(e) => props.onChange({ op: e.target.value as T["op"] } as Partial<T>)}>
          {digital ? (
            <>
              <option value="on">On</option>
              <option value="off">Off</option>
            </>
          ) : (
            <>
              <option value=">">Above</option>
              <option value="<">Below</option>
              <option value="between">Between</option>
            </>
          )}
        </select>
      </div>
      {!digital && (
        <div className="field">
          <label>Value{unit ? ` (${unit})` : ""}</label>
          <input inputMode="decimal" value={c.value ?? ""} onChange={(e) => props.onChange({ value: e.target.value === "" ? undefined : Number(e.target.value) } as Partial<T>)} />
        </div>
      )}
      {!digital && c.op === "between" && (
        <div className="field">
          <label>and</label>
          <input inputMode="decimal" value={c.value2 ?? ""} onChange={(e) => props.onChange({ value2: e.target.value === "" ? undefined : Number(e.target.value) } as Partial<T>)} />
        </div>
      )}
      <div className="field">
        <label>For (seconds)</label>
        <input inputMode="numeric" value={c.forSec} onChange={(e) => props.onChange({ forSec: Number(e.target.value) || 0 } as Partial<T>)} />
      </div>
      {props.extra}
      <RemoveBtn onRemove={props.onRemove} />
    </div>
  );
}

type OutPick = { devEui: string; kind: "do" | "ro" | "ao"; channel: number; port?: string | undefined };

function OutputPick({ devEui, kind, channel, port, boards, analog, onChange }: { devEui: string; kind: string; channel: number; port?: string | undefined; boards: Device[]; analog: boolean; onChange: (v: OutPick) => void }) {
  const d = boards.find((b) => b.devEui === devEui);
  const outs: Array<{ value: string; label: string; pick: OutPick }> =
    d && !isFmsBoard(d.model)
      ? milesightOutputs(d.model).map((o) => ({ value: o.port, label: `${portLabel(d, o.port)} (${o.port})`, pick: { devEui, kind: "do", channel: 0, port: o.port } }))
      : boardPorts(d?.model ?? "")
          .filter((p) => p.kind === "ro" || p.kind === "do" || (analog && p.kind === "ao"))
          .map((p) => {
            const k = `${p.kind}${p.channel}`;
            return { value: k, label: `${d ? portLabel(d, k) : k} (${k})`, pick: { devEui, kind: p.kind as "do" | "ro" | "ao", channel: p.channel, port: undefined } };
          });
  const current = port ?? `${kind}${channel}`;
  const firstOf = (eui: string): OutPick => {
    const b = boards.find((x) => x.devEui === eui);
    const ms = b && !isFmsBoard(b.model) ? milesightOutputs(b.model)[0] : undefined;
    return ms ? { devEui: eui, kind: "do", channel: 0, port: ms.port } : { devEui: eui, kind: "ro", channel: 1, port: undefined };
  };
  return (
    <>
      <div className="field">
        <label>Device</label>
        <select value={devEui} onChange={(e) => onChange(firstOf(e.target.value))}>
          {!d && <option value={devEui}>Not on this farm ({devEui})</option>}
          {boards.map((b) => (
            <option key={b.devEui} value={b.devEui}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Output</label>
        <select value={current} onChange={(e) => onChange(outs.find((o) => o.value === e.target.value)?.pick ?? firstOf(devEui))}>
          {!outs.some((o) => o.value === current) && <option value={current}>{current} (not on this device)</option>}
          {outs.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

function Actions({ list, boards, onChange, add }: { list: Action[]; boards: Device[]; onChange: (v: Action[]) => void; add: () => void }) {
  const upd = (i: number, v: Partial<Action>) => onChange(list.map((x, j) => (j === i ? { ...x, ...v } : x)));
  return (
    <>
      {list.map((a, i) => (
        <div className="frow cond" key={i}>
          <OutputPick
            devEui={a.devEui}
            kind={a.kind}
            channel={a.channel}
            port={a.port}
            boards={boards}
            analog
            onChange={(v) => upd(i, { ...v, ...(v.kind === "ao" ? { value: a.value ?? 0, state: undefined } : { state: a.state ?? true, value: undefined }) })}
          />
          {a.kind === "ao" ? (
            <div className="field">
              <label>Set to</label>
              <input inputMode="decimal" value={a.value ?? ""} onChange={(e) => upd(i, { value: Number(e.target.value) })} />
            </div>
          ) : (
            <div className="field">
              <label>Turn</label>
              <select value={a.state ? "on" : "off"} onChange={(e) => upd(i, { state: e.target.value === "on" })}>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </div>
          )}
          <Duration label="Wait first" hint={WAIT_TIP.action} sec={a.waitSec} onChange={(v) => upd(i, { waitSec: v })} />
          <RemoveBtn onRemove={() => onChange(list.filter((_, j) => j !== i))} />
        </div>
      ))}
      <div className="toolbar">
        <button className="btn" disabled={!boards.length} onClick={add}>
          + Action
        </button>
      </div>
    </>
  );
}

const WAIT_TIP = {
  step: "When this step starts, counted from when the steps begin (after the start actions), not from the step before. E.g. 15 min zones start at 0, 15, 30 and 45 min to run back to back. Start the next zone a minute before this one ends to overlap them, so the pump never pushes against shut valves.",
  action: "Counts from the action before it; the first one counts from when this part of the program begins. Leave at 0 for straight away.",
  run: "How long this output stays on, counted from when the device confirms it's on.",
};

function StepRow({ s, boards, onChange, onRemove }: { s: Step; boards: Device[]; onChange: (s: Step) => void; onRemove: () => void }) {
  return (
    <div className="frow cond">
      <OutputPick
        devEui={s.devEui}
        kind={s.kind}
        channel={s.channel}
        port={s.port}
        boards={boards}
        analog={false}
        onChange={(v) => onChange({ ...s, devEui: v.devEui, kind: v.kind === "ao" ? "ro" : v.kind, channel: v.channel, port: v.port })}
      />
      <Duration label="Run for" hint={WAIT_TIP.run} sec={s.runMin * 60} onChange={(v) => onChange({ ...s, runMin: v / 60 })} />
      <Duration label="Starts at" hint={WAIT_TIP.step} sec={s.waitSec} onChange={(v) => onChange({ ...s, waitSec: v })} />
      <RemoveBtn onRemove={onRemove} />
      <div className="muted small step-when">
        On at {clockText(s.waitSec)}
        {s.untilStop ? ", stays on until the program stops" : `, off at ${clockText(s.waitSec + s.runMin * 60)}`} (from when the steps begin)
      </div>
    </div>
  );
}

/**
 * A time as hours, minutes and seconds (the program keeps run times in minutes and waits in
 * seconds; this shows both the same way). 90 typed in minutes is fine: it's counted as 1 h 30 min.
 */
function Duration({ label, hint, sec, onChange }: { label: string; hint?: string; sec: number; onChange: (sec: number) => void }) {
  const t = hms(sec);
  const [draft, setDraft] = useState<{ h: string; m: string; s: string } | null>(null);
  const shown = draft ?? { h: String(t.h), m: String(t.m), s: String(t.s) };
  const set = (k: "h" | "m" | "s", v: string) => {
    const next = { ...shown, [k]: v.replace(/[^\d]/g, "") };
    setDraft(next);
    onChange((Number(next.h) || 0) * 3600 + (Number(next.m) || 0) * 60 + (Number(next.s) || 0));
  };
  return (
    <div className="field">
      <label>
        {label}
        {hint ? <HelpTip text={hint} label={`About ${label.toLowerCase()}`} /> : null}
      </label>
      <div className="hms" onBlur={() => setDraft(null)}>
        {(["h", "m", "s"] as const).map((k) => (
          <span key={k}>
            <input inputMode="numeric" value={shown[k]} onFocus={(e) => e.target.select()} onChange={(e) => set(k, e.target.value)} aria-label={`${label}: ${{ h: "hours", m: "minutes", s: "seconds" }[k]}`} />
            <span className="muted small">{{ h: "hr", m: "min", s: "sec" }[k]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function RemoveBtn({ onRemove }: { onRemove: () => void }) {
  return (
    <button className="btn ghost remove" onClick={onRemove} aria-label="Remove">
      Remove
    </button>
  );
}
